import { Controller } from '@hotwired/stimulus';
import * as Turbo from '@hotwired/turbo';

/**
 * Délai maximal (ms) pendant lequel un clic sur un lien attend le PATCH « vu »
 * avant de naviguer. Sans cette attente, la page d'arrivée peut calculer
 * `modal_due` avant que le PATCH soit commité, et la fenêtre se rouvre.
 */
const SEEN_WAIT_MS = 1000;

/**
 * Promesse du PATCH « vu », partagée au niveau du MODULE : elle survit à une
 * reconnexion du controller (morph Turbo, `data-turbo-permanent`), donc un
 * PATCH en cours ou réussi n'est pas renvoyé. Elle est remise à `null` si le
 * PATCH échoue (erreur réseau ou réponse non 2xx, par exemple un jeton CSRF
 * expiré) et à chaque `turbo:load` : sans ça, une fenêtre revenue après un
 * échec ou une nouvelle nouveauté publiée ne serait plus jamais marquée vue
 * avant un rechargement complet. Un PATCH en double est sans effet (idempotent).
 * @type {Promise<Response|undefined>|null}
 */
let seenPromise = null;

/**
 * Whats New Controller (« Quoi de neuf sur Jerico »)
 *
 * Pilote la fenêtre des nouveautés rendue par
 * `ProductUpdates::WhatsNewModalComponent` : navigation pas à pas entre les
 * cartes, et marquage « vu » côté serveur à la fermeture ou au clic sur un
 * lien de la fenêtre. Monté sur la racine de la modale, à côté du controller
 * `modal` (`data-controller="modal whats-new"`), qu'il ne modifie pas.
 *
 * Aucune couleur ni SVG n'est généré ici (DESIGN.md #18) : le JS ne fait que
 * basculer les attributs `hidden` et `data-current` (jamais une classe).
 *
 * Targets:
 * - step: une carte (`role="group"`), toutes `hidden` sauf la première ;
 *   son titre (`h4`) porte `tabindex="-1"` et reçoit le focus à chaque
 *   changement (à défaut, la carte elle-même). Le sélecteur vise le titre et
 *   jamais le lien de l'illustration, lui aussi en `tabindex="-1"`
 * - previous: bouton « Précédent » (caché sur la première carte)
 * - next: bouton « Suivant » (caché sur la dernière carte)
 * - done: bouton « J'ai compris » (visible seulement sur la dernière carte)
 * - dot: point de position, un par carte (`aria-hidden` sur leur conteneur),
 *   absents avec une seule carte ; le point courant porte `data-current`
 *
 * Values:
 * - seenUrl: URL du PATCH « vu » (`modal_seen_product_updates_path`) ;
 *   chaîne vide = aperçu back-office, aucun appel réseau, aucun lien intercepté
 *
 * Actions:
 * - previous / next: carte précédente / suivante
 * - markSeen: sur `modal:close` (Échap, croix, fond, « J'ai compris »)
 * - markSeenOnLink: sur tout clic dans la fenêtre ; réagit à tout `a[href]`
 *   (titre et illustration de chaque étape, « Toutes les nouveautés »)
 *
 * @example
 * <div id="whats-new-modal"
 *      data-controller="modal whats-new"
 *      data-action="modal:close->whats-new#markSeen click->whats-new#markSeenOnLink"
 *      data-whats-new-seen-url-value="/nouveautes/fenetre-vue">
 *   <div role="group" aria-label="Nouveauté 1 sur 2" data-whats-new-target="step">
 *     <h4 tabindex="-1">Titre</h4>
 *   </div>
 *   <div role="group" aria-label="Nouveauté 2 sur 2" data-whats-new-target="step" hidden>
 *     <h4 tabindex="-1">Titre</h4>
 *   </div>
 *   <div aria-hidden="true">
 *     <span data-whats-new-target="dot" data-current></span>
 *     <span data-whats-new-target="dot"></span>
 *   </div>
 *   <button data-whats-new-target="previous" data-action="click->whats-new#previous" hidden>Précédent</button>
 *   <button data-whats-new-target="next" data-action="click->whats-new#next">Suivant</button>
 *   <button data-whats-new-target="done" data-action="modal#close" hidden>J'ai compris</button>
 * </div>
 */
export default class extends Controller {
  static targets = ['step', 'previous', 'next', 'done', 'dot'];
  static values = {
    seenUrl: { type: String, default: '' },
  };

  connect() {
    // Chaque visite Turbo repart d'un état « pas encore marqué » : si le
    // serveur renvoie la fenêtre, c'est que le marquage précédent n'a pas pris
    // ou qu'une nouvelle nouveauté est en avant.
    this.resetSeenHandler = () => {
      seenPromise = null;
    };
    document.addEventListener('turbo:load', this.resetSeenHandler);

    this.index = 0;
    // État initial cohérent sans voler le focus : la modale est encore fermée,
    // c'est le controller `modal` qui place le focus à l'ouverture.
    this.go(0, { focus: false });
  }

  disconnect() {
    document.removeEventListener('turbo:load', this.resetSeenHandler);
    // La promesse « vu » reste au niveau du module : une reconnexion (morph)
    // ne renvoie pas un PATCH déjà en cours ou réussi.
  }

  previous(event) {
    event?.preventDefault();
    this.go(this.index - 1);
  }

  next(event) {
    event?.preventDefault();
    this.go(this.index + 1);
  }

  /**
   * Affiche la carte `index` et met à jour pied et points de position.
   * @param {number} index
   * @param {{focus?: boolean}} [options]
   */
  go(index, { focus = true } = {}) {
    const steps = this.stepTargets;
    const count = steps.length;
    if (count === 0) return;

    const current = Math.min(Math.max(index, 0), count - 1);
    const isFirst = current === 0;
    const isLast = current === count - 1;
    this.index = current;

    steps.forEach((step, i) => {
      step.hidden = i !== current;
    });

    if (this.hasPreviousTarget) this.previousTarget.hidden = isFirst;
    if (this.hasNextTarget) this.nextTarget.hidden = isLast;
    if (this.hasDoneTarget) this.doneTarget.hidden = !isLast;
    this.dotTargets.forEach((dot, i) => {
      dot.toggleAttribute('data-current', i === current);
    });

    if (focus) {
      const step = steps[current];
      (step.querySelector('h4[tabindex="-1"]') ?? step).focus();
    }
  }

  /**
   * Marque la fenêtre vue. Réutilise le PATCH en cours ou réussi ; après un
   * échec, le prochain appel renvoie un PATCH. La promesse est toujours
   * résolue, même en cas d'erreur réseau.
   * @returns {Promise<Response|undefined>}
   */
  markSeen() {
    if (!this.seenUrlValue) return Promise.resolve(undefined);
    if (seenPromise) return seenPromise;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';
    seenPromise = fetch(this.seenUrlValue, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': csrfToken, Accept: 'application/json' },
      credentials: 'same-origin',
      keepalive: true,
    })
      .then((response) => {
        if (!response.ok) seenPromise = null;
        return response;
      })
      .catch(() => {
        seenPromise = null;
        return undefined;
      });

    return seenPromise;
  }

  /**
   * Clic dans la fenêtre : sur un `a[href]`, marque la fenêtre vue avant de
   * naviguer. Les liens externes, les nouveaux onglets (modificateurs,
   * `target`) et `download` naviguent nativement ; on marque seulement.
   * @param {MouseEvent} event
   */
  async markSeenOnLink(event) {
    if (!this.seenUrlValue) return;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || !this.element.contains(link)) return;

    const url = new URL(link.getAttribute('href'), window.location.href);
    const isHttp = url.protocol === 'http:' || url.protocol === 'https:';
    const opensElsewhere =
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.hasAttribute('download') ||
      (link.target && link.target !== '_self');

    if (!isHttp || url.origin !== window.location.origin || opensElsewhere) {
      this.markSeen();
      return;
    }

    event.preventDefault();
    // Le fetch n'est PAS annulé au bout du délai (keepalive le laisse finir
    // après la navigation) : on borne seulement l'attente.
    await Promise.race([
      this.markSeen(),
      new Promise((resolve) => setTimeout(resolve, SEEN_WAIT_MS)),
    ]);
    Turbo.visit(url.href);
  }
}
